import { useState, useEffect, useCallback } from "react";
import {
  X, FolderPlus, Folder, Edit2, Trash2, RefreshCw, CheckCircle, AlertCircle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl text-sm font-semibold shadow-2xl
      ${type === "success" ? "bg-[#111] border border-[#C9A84C]/40 text-white" : "bg-red-950 border border-red-500/40 text-red-300"}`}>
      {type === "success" ? <CheckCircle size={16} className="text-[#C9A84C]" /> : <AlertCircle size={16} className="text-red-400" />}
      {msg}
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100"><X size={14} /></button>
    </div>
  );
}

function CreateFolderModal({ token, onClose, onCreated, editFolder }) {
  const [naam, setNaam] = useState(editFolder?.naam || "");
  const [kleur, setKleur] = useState(editFolder?.kleur || "#C9A84C");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const KLEUREN = ["#C9A84C", "#3b82f6", "#10b981", "#f43f5e", "#8b5cf6", "#f97316", "#06b6d4"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!naam.trim()) { setError("Geef een naam op"); return; }
    setLoading(true); setError("");
    try {
      const url = editFolder ? `${API}/api/company/schema-folders/${editFolder.id}` : `${API}/api/company/schema-folders`;
      const method = editFolder ? "PUT" : "POST";
      const r = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ naam, kleur }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Mislukt");
      onCreated();
      onClose();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/10 rounded-2xl p-7 max-w-sm w-full animate-fadeIn">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-white font-bold text-lg">{editFolder ? "Map bewerken" : "Map aanmaken"}</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <label className="block text-zinc-400 text-xs font-semibold mb-1.5 uppercase tracking-wide">Naam</label>
          <input value={naam} onChange={e => setNaam(e.target.value)} placeholder="Beginners, Afvallen, Kracht..."
            className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm mb-4 focus:outline-none focus:border-[#C9A84C]/50"
            data-testid="folder-naam-input" />

          <label className="block text-zinc-400 text-xs font-semibold mb-2 uppercase tracking-wide">Kleur</label>
          <div className="flex gap-2 mb-5">
            {KLEUREN.map(c => (
              <button key={c} type="button" onClick={() => setKleur(c)}
                className={`w-7 h-7 rounded-full transition-transform ${kleur === c ? "scale-125 ring-2 ring-white/30" : "hover:scale-110"}`}
                style={{ background: c }} />
            ))}
          </div>

          {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-3 bg-zinc-800/50 border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl">Annuleren</button>
            <button type="submit" disabled={loading} data-testid="create-folder-submit-btn"
              className="flex-1 py-3 text-black font-bold text-sm rounded-xl disabled:opacity-40"
              style={{ background: "#C9A84C" }}>
              {loading ? "Opslaan..." : editFolder ? "Bijwerken" : "Aanmaken"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function MappenTab({ token }) {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editFolder, setEditFolder] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => setToast({ msg, type });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/company/schema-folders`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setFolders(d.folders || []);
    } catch { showToast("Laden mislukt", "error"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    try {
      const r = await fetch(`${API}/api/company/schema-folders/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Verwijderen mislukt");
      setDeleteConfirm(null);
      showToast("Map verwijderd");
      load();
    } catch (err) { showToast(err.message, "error"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Organisatie</p>
          <h2 className="text-xl font-bold text-white">Schema Mappen</h2>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="add-folder-btn"
          className="flex items-center gap-2 px-4 py-2.5 text-black font-bold text-sm rounded-xl" style={{ background: "#C9A84C" }}>
          <FolderPlus size={14} /> Map aanmaken
        </button>
      </div>

      <p className="text-zinc-500 text-sm mb-6">Organiseer je schema's in mappen voor een overzichtelijke structuur. Bijv. Beginners, Afvallen, Kracht, of specifieke programma's.</p>

      {loading ? (
        <div className="text-center py-16 text-zinc-600"><RefreshCw size={20} className="animate-spin mx-auto" /></div>
      ) : folders.length === 0 ? (
        <div className="text-center py-16">
          <Folder size={40} className="text-zinc-800 mx-auto mb-3" />
          <p className="text-zinc-500 font-semibold mb-1">Nog geen mappen</p>
          <p className="text-zinc-600 text-sm mb-4">Maak mappen aan om schema's overzichtelijk te organiseren.</p>
          <button onClick={() => setShowCreate(true)} className="px-5 py-2.5 text-black font-bold text-sm rounded-xl" style={{ background: "#C9A84C" }}>
            Eerste map aanmaken
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {folders.map(f => (
            <div key={f.id} className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-5 hover:border-white/10 transition-colors group">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: f.kleur + "20", border: `1.5px solid ${f.kleur}40` }}>
                  <Folder size={18} style={{ color: f.kleur }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold text-sm truncate">{f.naam}</h3>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className="w-3 h-3 rounded-full" style={{ background: f.kleur }} />
                    <span className="text-zinc-600 text-xs">{f.kleur}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 pt-3 border-t border-white/5">
                <button onClick={() => setEditFolder(f)} data-testid={`edit-folder-${f.id}`}
                  className="flex-1 py-2 text-xs font-semibold text-zinc-400 hover:text-white bg-white/4 hover:bg-white/8 rounded-lg transition-colors flex items-center justify-center gap-1">
                  <Edit2 size={11} /> Bewerken
                </button>
                <button onClick={() => setDeleteConfirm(f)} data-testid={`delete-folder-${f.id}`}
                  className="py-2 px-3 text-xs text-zinc-600 hover:text-red-400 bg-white/4 hover:bg-red-500/10 rounded-lg transition-colors">
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showCreate || editFolder) && (
        <CreateFolderModal
          token={token}
          editFolder={editFolder}
          onClose={() => { setShowCreate(false); setEditFolder(null); }}
          onCreated={() => { load(); showToast(editFolder ? "Map bijgewerkt!" : "Map aangemaakt!"); }}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-7 max-w-sm w-full">
            <h3 className="text-white font-bold text-lg mb-2">Map verwijderen</h3>
            <p className="text-zinc-400 text-sm mb-1">Weet je zeker dat je <strong className="text-white">{deleteConfirm.naam}</strong> wilt verwijderen?</p>
            <p className="text-zinc-600 text-xs mb-6">Schema's in deze map worden niet verwijderd, maar hun map-koppeling wordt verwijderd.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 bg-zinc-800/50 border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl">Annuleren</button>
              <button onClick={() => handleDelete(deleteConfirm.id)} className="flex-1 py-2.5 bg-red-900/40 border border-red-500/30 text-red-400 font-semibold text-sm rounded-xl">Verwijderen</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
